// Eskom PARCC — dashboard derivations the domain KPIs don't already cover.
//
// `computeDashboard(transactions)` (src/domain/dashboard.ts) is the single source
// of the transaction-level KPI maths (totals, reviewed/compliant, possible-IE
// count + value, pending value, records-with-findings, risk score, byLevel /
// byMechanism / byDivision). It does NOT see the findings register, so the
// finding-derived sections — finding-line count, anchor mix and recurring theme
// breakdown — are derived here from the (already-fetched, already-filtered)
// findings array. These are small, pure helpers so they can be unit-tested in
// isolation without duplicating the computeDashboard tests.
//
// Everything else the dashboard needs (Escalate / Exposure-at-risk / Self-declared
// focus tiles, the division heatmap, the compliance distribution) is folded out
// of computeDashboard's `byLevel` / `byDivision` in the page itself.
import type { Finding, Transaction } from '../../data/types';
import { isFindingLevel, riskWeight } from '../../domain/dashboard';
import { canonicalDivision } from '../../domain/divisions';
import { findingDisposition } from '../../domain/findings';
import { themeFor } from '../../domain/themes/themeRules';

/**
 * The subset of finding fields the finding-derived helpers read. Declared
 * structurally so both the full domain {@link Finding} and the read projection
 * (`FindingRead`, which drops the write-only `transactionId` / `rowVersion`) flow
 * in without a cast. `route` / `text` are optional — only the recurring-theme
 * mapper reads them, and analyst-captured / imported lines may carry neither. The
 * compliance/status fields let {@link breachBreakdown} decide a line's disposition
 * (outstanding vs addressed) without a transaction join.
 */
export type FindingLike = Pick<Finding, 'id' | 'referenceNo' | 'anchors' | 'matchedRules' | 'deleted'> &
  Partial<Pick<Finding, 'route' | 'text' | 'effectiveStatus' | 'legacyStatus' | 'finalCompliance' | 'suggestedCompliance'>>;

/** Dataset-type categories, used by the Findings page's "Category" filter. */
export const DATASET_CATEGORIES = ['Contracts', 'Panels', 'POs', 'LPOs', 'Task Orders', 'Other'] as const;
export type DatasetCategory = (typeof DATASET_CATEGORIES)[number];

/**
 * Map a transaction's dataset to its coarse category.
 *
 * ORDER IS LOAD-BEARING — every test here is a substring match, so the specific
 * cases must run before the general ones:
 *   • 'task'  first, so the retired 'NTCSA Task Orders' is not read as a PO;
 *   • 'lpo'   before 'po', because 'LPOs'.includes('po') is true;
 *   • 'panel' before 'contract', so a panel release stops being a Contract
 *     (R6 — before this it matched nothing and silently landed in 'Other').
 * The retired 'POs / LPOs' is genuinely ambiguous — it cannot be split without
 * the row's reference number, which this function does not see. It resolves to
 * 'LPOs' by substring order, which is arbitrary but beats 'Other'; the real
 * answer is to run scripts/splitDatasetBuckets.mjs so no row carries it.
 */
export function datasetCategoryFor(dataset: string | undefined): DatasetCategory {
  const ds = (dataset ?? '').toLowerCase();
  if (ds.includes('task')) return 'Task Orders';
  if (ds.includes('panel')) return 'Panels';
  if (ds.includes('lpo')) return 'LPOs';
  if (ds.includes('po')) return 'POs';
  if (ds.includes('contract')) return 'Contracts';
  return 'Other';
}

/** The three regulatory anchors the monolith dashboard surfaces, in display order. */
export const DASHBOARD_ANCHORS = ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'] as const;
export type DashboardAnchor = (typeof DASHBOARD_ANCHORS)[number];

export interface AnchorBucket {
  anchor: string;
  /** Number of finding lines selecting this anchor. */
  lines: number;
  /** Distinct transactions (by referenceNo) selecting this anchor. */
  transactions: number;
}

/**
 * Count finding lines and distinct transactions per regulatory anchor.
 *
 * Mirrors the monolith's anchor breakdown: a finding contributes one *line* per
 * distinct anchor it carries, and its transaction is counted once per anchor.
 * The three primary anchors are always present (zero if unused) and ordered by
 * line count descending, ties broken by the canonical order in
 * {@link DASHBOARD_ANCHORS}; any other anchor seen in the data is appended.
 */
export function anchorBreakdown(findings: readonly FindingLike[]): AnchorBucket[] {
  const lines = new Map<string, number>();
  const txns = new Map<string, Set<string>>();
  for (const anchor of DASHBOARD_ANCHORS) {
    lines.set(anchor, 0);
    txns.set(anchor, new Set());
  }

  for (const f of findings) {
    if (f.deleted) continue;
    const ref = f.referenceNo ?? f.id;
    // De-duplicate anchors within a single finding so one line counts once.
    for (const anchor of new Set(f.anchors ?? [])) {
      if (!anchor) continue;
      lines.set(anchor, (lines.get(anchor) ?? 0) + 1);
      const set = txns.get(anchor) ?? new Set<string>();
      set.add(ref);
      txns.set(anchor, set);
    }
  }

  const order = new Map(DASHBOARD_ANCHORS.map((a, i) => [a as string, i]));
  return [...lines.keys()]
    .map((anchor) => ({
      anchor,
      lines: lines.get(anchor) ?? 0,
      transactions: txns.get(anchor)?.size ?? 0,
    }))
    .sort((a, b) => {
      if (b.lines !== a.lines) return b.lines - a.lines;
      const ai = order.get(a.anchor) ?? Number.MAX_SAFE_INTEGER;
      const bi = order.get(b.anchor) ?? Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
}

export interface ThemeBucket {
  theme: string;
  count: number;
}

/**
 * Recurring finding-theme breakdown, derived from the rule identifiers each
 * finding matched (`matchedRules`). A theme is counted once per *transaction*
 * that has at least one finding matching that rule, so the count reads as
 * "transactions affected by this theme" rather than raw line hits. Returns the
 * top {@link limit} themes by affected-transaction count, ties broken
 * alphabetically for a stable order.
 */
export function themeBreakdown(findings: readonly FindingLike[], limit = 6): ThemeBucket[] {
  const txnsByTheme = new Map<string, Set<string>>();
  for (const f of findings) {
    if (f.deleted) continue;
    const ref = f.referenceNo ?? f.id;
    for (const rule of new Set(f.matchedRules ?? [])) {
      if (!rule) continue;
      const set = txnsByTheme.get(rule) ?? new Set<string>();
      set.add(ref);
      txnsByTheme.set(rule, set);
    }
  }
  return [...txnsByTheme.entries()]
    .map(([theme, set]) => ({ theme, count: set.size }))
    .filter((t) => t.count > 0)
    .sort((a, b) => (b.count !== a.count ? b.count - a.count : a.theme.localeCompare(b.theme)))
    .slice(0, limit);
}

/**
 * Recurring findings by CURATED THEME (Lindiwe feedback Jun 23 2026, item 1b):
 * replaces the "recurring compliance trend" with the common themes the findings
 * recur under (Tax, Validity, CSD, Statutory documents, DAA/Award, COI, …). Each
 * finding is mapped to its theme(s) via {@link themeFor}, and a theme is counted
 * once per *transaction* that has at least one finding in it — so the count reads
 * as "transactions exhibiting this theme" (true recurrence across the population),
 * not raw line hits. Returns the top {@link limit} themes by affected-transaction
 * count, ties broken alphabetically. 'Other' is included only when it actually
 * recurs (so the chart is not dominated by an uninformative bucket when avoidable,
 * but never hides volume).
 */
export function recurringFindingsByTheme(findings: readonly FindingLike[], limit = 8): ThemeBucket[] {
  const txnsByTheme = new Map<string, Set<string>>();
  for (const f of findings) {
    if (f.deleted) continue;
    const ref = f.referenceNo ?? f.id;
    for (const theme of new Set(themeFor(f))) {
      const set = txnsByTheme.get(theme) ?? new Set<string>();
      set.add(ref);
      txnsByTheme.set(theme, set);
    }
  }
  return [...txnsByTheme.entries()]
    .map(([theme, set]) => ({ theme, count: set.size }))
    .filter((t) => t.count > 0)
    .sort((a, b) => (b.count !== a.count ? b.count - a.count : a.theme.localeCompare(b.theme)))
    .slice(0, limit);
}

export interface DatasetCoverage {
  dataset: string;
  records: number;
  withFindings: number;
  value: number;
  /** Percentage of this dataset's records carrying findings (0–100, rounded). */
  coveragePct: number;
}

/**
 * Per-dataset population coverage: how many records each source dataset has and
 * how much of it carries findings, plus total Rand value. "Carries a finding"
 * is evidenced two ways (matching the monolith): the transaction's derived level
 * has a risk weight, OR the finding register links a line to its referenceNo.
 * Sorted by record count descending so the largest sources lead.
 */
export function datasetCoverage(
  transactions: readonly Transaction[],
  findings: readonly FindingLike[],
): DatasetCoverage[] {
  const refsWithFindings = referencesWithFindings(findings);
  const buckets = new Map<string, { records: number; withFindings: number; value: number }>();

  for (const t of transactions) {
    if (t.deleted) continue;
    const dataset = t.dataset || 'Other';
    const bucket = buckets.get(dataset) ?? { records: 0, withFindings: 0, value: 0 };
    bucket.records += 1;
    bucket.value += Number(t.value || 0);
    if (isFindingLevel(t.complianceLevel) || refsWithFindings.has(t.referenceNo)) {
      bucket.withFindings += 1;
    }
    buckets.set(dataset, bucket);
  }

  return [...buckets.entries()]
    .map(([dataset, b]) => ({
      dataset,
      records: b.records,
      withFindings: b.withFindings,
      value: b.value,
      coveragePct: b.records > 0 ? Math.round((b.withFindings / b.records) * 100) : 0,
    }))
    .sort((a, b) => (b.records !== a.records ? b.records - a.records : a.dataset.localeCompare(b.dataset)));
}

/**
 * Distinct in-view transaction references that carry at least one finding line.
 * Useful where the finding register, not the transaction's derived level, is the
 * evidence of a finding.
 */
export function referencesWithFindings(findings: readonly FindingLike[]): Set<string> {
  const refs = new Set<string>();
  for (const f of findings) {
    if (f.deleted) continue;
    if (f.referenceNo) refs.add(f.referenceNo);
  }
  return refs;
}

// Levels that do NOT count as flagged (cleared / not-applicable / pending /
// lifecycle markers) — anything else is a flagged classification.
const NON_FLAGGED_LEVELS = new Set([
  'Compliant',
  'Not Applicable',
  'Self-Declared: Not Applicable',
  'Pending Review',
  'Not Reviewed',
  'Deleted',
  'To delete',
]);

export interface DivisionStat {
  division: string;
  records: number;
  flagged: number;
  value: number;
  flaggedValue: number;
  /** Mean risk weight across the division's records (0–3). */
  riskIndex: number;
}

/**
 * Per-division risk profile: record + flagged counts, total + flagged Rand
 * value, and a mean risk-weight index (Possible IE = 3, Policy = 2, Housekeeping
 * = 1). Sorted by risk index descending and capped at {@link limit} divisions —
 * matching the monolith's "division risk heatmap". Derived from transactions
 * directly because computeDashboard's `byDivision` is a plain count map with no
 * per-level / per-value breakdown.
 */
export function divisionStats(transactions: readonly Transaction[], limit = 8): DivisionStat[] {
  const buckets = new Map<
    string,
    { records: number; flagged: number; value: number; flaggedValue: number; weight: number }
  >();

  for (const t of transactions) {
    if (t.deleted) continue;
    // Group by the CANONICAL division so spelling variants ("GX- Head Office" and
    // "Gx", the "ICT and Professional Servises" typo, …) roll up under one bar
    // instead of splitting the heatmap (Lindiwe R4 feedback #3).
    const division = canonicalDivision(t.division) || 'Unspecified';
    const level = t.complianceLevel || 'Pending Review';
    const value = Number(t.value || 0);
    const bucket =
      buckets.get(division) ?? { records: 0, flagged: 0, value: 0, flaggedValue: 0, weight: 0 };
    bucket.records += 1;
    bucket.value += value;
    bucket.weight += riskWeight(level);
    if (!NON_FLAGGED_LEVELS.has(level)) {
      bucket.flagged += 1;
      bucket.flaggedValue += value;
    }
    buckets.set(division, bucket);
  }

  return [...buckets.entries()]
    .map(([division, b]) => ({
      division,
      records: b.records,
      flagged: b.flagged,
      value: b.value,
      flaggedValue: b.flaggedValue,
      riskIndex: b.records > 0 ? b.weight / b.records : 0,
    }))
    .filter((d) => d.records > 0)
    .sort((a, b) => (b.riskIndex !== a.riskIndex ? b.riskIndex - a.riskIndex : b.records - a.records))
    .slice(0, limit);
}

// ---- Breach reporting (owner feedback, item 1) ----------------------------
//
// The rulebook anchor on a finding is mapped to one of three management-facing
// breach categories so the dashboard can report "policy breaches / PFMA
// contraventions / legislative breaches". A finding may carry anchors that fall
// in more than one bucket → it is counted in EACH. Anchors not listed below
// (National Treasury / Governance / Administrative Control) are deliberately
// UNMAPPED pending owner sign-off — they contribute to no breach bucket.

/** The three breach buckets, each owning the anchor strings that feed it. */
export const BREACH_BUCKETS = {
  policy: ['Eskom Policies & Procedures', 'Internal Policy', 'Procedure', 'Procurement Instruction'],
  pfma: ['PFMA'],
  legislative: ['Legislation', 'Constitution', 'Statutory Requirement', 'Relevant Laws & Regulations'],
} as const;

export type BreachBucketKey = keyof typeof BREACH_BUCKETS;

/** Reverse lookup: anchor string → the bucket(s) it belongs to. */
const ANCHOR_TO_BUCKETS: ReadonlyMap<string, BreachBucketKey[]> = (() => {
  const m = new Map<string, BreachBucketKey[]>();
  for (const key of Object.keys(BREACH_BUCKETS) as BreachBucketKey[]) {
    for (const anchor of BREACH_BUCKETS[key]) {
      const existing = m.get(anchor) ?? [];
      existing.push(key);
      m.set(anchor, existing);
    }
  }
  return m;
})();

export interface BreachBucket {
  key: BreachBucketKey;
  /** Human label for the card/chart. */
  label: string;
  /** Identified breach lines whose anchors fall in this bucket (outstanding + addressed). */
  lines: number;
  /**
   * Lines on findings still OUTSTANDING (not self-declared, not resolved) — the
   * breach count managers read, on the same basis as the Findings scorecard
   * (Lindiwe R9, Aug 2026: a declared breach is not counted as an outstanding
   * breach). A line anchored to more than one framework counts once per bucket.
   */
  outstanding: number;
  /** lines − outstanding: lines the buyer resolved or closed by self-declaration. */
  addressed: number;
  /** Distinct transactions (by referenceNo) carrying an OUTSTANDING line in this bucket. */
  transactions: number;
}

const BREACH_LABELS: Record<BreachBucketKey, string> = {
  policy: 'Policy breaches',
  pfma: 'PFMA contraventions',
  legislative: 'Legislative / regulatory breaches',
};

/**
 * Shared footnote for the outstanding/addressed breach split (WS2 Lindiwe R4,
 * re-based to the closure model in R9). One constant so the dashboard card, the
 * report PPTX and the report PDF cannot drift. British English. "Addressed" is
 * honest — the buyer either fixed the line or declared it; a declared breach is
 * acknowledged, not recovered — and the count reconciles with the Findings tile.
 */
export const BREACH_SPLIT_FOOTNOTE =
  'Outstanding (highlighted) counts breach lines still to be resolved — the same basis as the Findings count. ' +
  'Addressed (muted) covers lines the buyer resolved or closed by self-declaration (declared, not recovered). ' +
  'A line anchored to more than one framework counts in each.';

/**
 * Count breach lines + distinct transactions per breach bucket (item 1's
 * dashboard reporting), split by disposition. A finding contributes one line per
 * bucket it touches (de-duped within the finding). `outstanding` counts only lines
 * not yet resolved — decided via {@link findingDisposition}, where a finding on a
 * self-declared transaction (`selfDeclaredRefs`) is cleared — and `transactions`
 * counts distinct transactions carrying such a line, so both read as real,
 * unresolved breaches. Always returns the three buckets in a stable order.
 */
export function breachBreakdown(
  findings: readonly FindingLike[],
  selfDeclaredRefs?: ReadonlySet<string>,
): BreachBucket[] {
  const lines = new Map<BreachBucketKey, number>();
  const outstanding = new Map<BreachBucketKey, number>();
  const txns = new Map<BreachBucketKey, Set<string>>();
  for (const key of Object.keys(BREACH_BUCKETS) as BreachBucketKey[]) {
    lines.set(key, 0);
    outstanding.set(key, 0);
    txns.set(key, new Set());
  }

  for (const f of findings) {
    if (f.deleted) continue;
    const ref = f.referenceNo ?? f.id;
    const isOutstanding =
      findingDisposition(f, selfDeclaredRefs?.has(f.referenceNo ?? '') ?? false) === 'outstanding';
    // The buckets this finding touches, de-duped (so one line counts once per bucket).
    const buckets = new Set<BreachBucketKey>();
    for (const anchor of new Set(f.anchors ?? [])) {
      for (const key of ANCHOR_TO_BUCKETS.get(anchor) ?? []) buckets.add(key);
    }
    for (const key of buckets) {
      lines.set(key, (lines.get(key) ?? 0) + 1);
      if (isOutstanding) {
        outstanding.set(key, (outstanding.get(key) ?? 0) + 1);
        txns.get(key)?.add(ref);
      }
    }
  }

  return (Object.keys(BREACH_BUCKETS) as BreachBucketKey[]).map((key) => {
    const total = lines.get(key) ?? 0;
    const out = outstanding.get(key) ?? 0;
    return {
      key,
      label: BREACH_LABELS[key],
      lines: total,
      outstanding: out,
      addressed: total - out,
      transactions: txns.get(key)?.size ?? 0,
    };
  });
}

export interface TrendPoint {
  /** The audit period label (the transaction's year; 'Unknown' when absent). */
  period: string;
  /** Total finding lines recorded against transactions in this period. */
  total: number;
  /** Policy breach lines in this period. */
  policy: number;
  /** PFMA contravention lines in this period. */
  pfma: number;
  /** Legislative / regulatory breach lines in this period. */
  legislative: number;
}

/**
 * Recurring-compliance trend over time (item 1). Findings carry no own timestamp
 * in the read projection, so the time axis is the JOINED transaction's `year`
 * (the business-meaningful audit period); a finding whose transaction is missing
 * or has no year falls in 'Unknown'. Each point carries the total finding lines
 * and the per-breach-bucket line counts, chart-ready. Sorted chronologically by
 * period label ('Unknown' last).
 */
export function trendByPeriod(
  findings: readonly FindingLike[],
  txByRef: ReadonlyMap<string, Pick<Transaction, 'year'>>,
): TrendPoint[] {
  const points = new Map<string, TrendPoint>();
  const pointFor = (period: string): TrendPoint => {
    let p = points.get(period);
    if (!p) {
      p = { period, total: 0, policy: 0, pfma: 0, legislative: 0 };
      points.set(period, p);
    }
    return p;
  };

  for (const f of findings) {
    if (f.deleted) continue;
    const ref = f.referenceNo ?? '';
    const year = (ref ? txByRef.get(ref)?.year : undefined)?.trim();
    const point = pointFor(year || 'Unknown');
    point.total += 1;
    const buckets = new Set<BreachBucketKey>();
    for (const anchor of new Set(f.anchors ?? [])) {
      for (const key of ANCHOR_TO_BUCKETS.get(anchor) ?? []) buckets.add(key);
    }
    for (const key of buckets) point[key] += 1;
  }

  return [...points.values()].sort((a, b) => {
    if (a.period === 'Unknown') return 1;
    if (b.period === 'Unknown') return -1;
    return a.period.localeCompare(b.period, undefined, { numeric: true });
  });
}
